import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { CleverService } from '../../services/clever.service';
import { Toast } from '../../components/Common/Toast';
import Loader from '../../components/Common/Loader';
import { callAPI } from '../../services/api.service';

interface CleverCallbackProps {}

const CleverCallback: React.FC<CleverCallbackProps> = ({}: CleverCallbackProps) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentMainURLQuery = useSelector((state: RootState) => state.app.currentMainURLQuery);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setIsLoading(true);

        const searchParams = new URLSearchParams(currentMainURLQuery);

        // Extract OAuth parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors
        if (error) {
          console.error('Clever OAuth error:', error, errorDescription);
          setError(errorDescription || 'OAuth authorization failed');
          return;
        }

        // Validate required parameters
        if (!code) {
          setError('Authorization code is missing');
          return;
        }

        console.log('Processing Clever callback with code:', code);

        // Handle the OAuth callback
        const authResponse = await CleverService.handleCallback(code, state || undefined);

        console.log('Auth response received:', authResponse);

        // Complete the authentication flow (signs in to Firebase and stores token)
        await CleverService.completeAuth(authResponse, dispatch);

        if (authResponse.isNewUser) {
          const tallyProjectId = localStorage.getItem('tally_project') || '';
          const leadId = localStorage.getItem('tally_lead_id') || '';
          if (tallyProjectId || leadId) {
            const email = authResponse.user?.email;
            if (email) {
              callAPI('api/auth/update-lead', {
                email,
                ...(tallyProjectId ? { tallyProjectId } : {}),
                ...(leadId ? { leadId } : {}),
              }).catch(console.error);
            }
            localStorage.removeItem('tally_project');
            localStorage.removeItem('tally_lead_id');
          }
        }

        Toast(authResponse.isNewUser ? 'Account created successfully!' : 'Login successful!');
      } catch (error: any) {
        console.error('Error handling Clever callback:', error);
        setError(error.message || 'Authentication failed');
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, [currentMainURLQuery, navigate, dispatch]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-whiteGrayBackground flex flex-col justify-center items-center font-sofiaSans">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <Loader />
          <h2 className="text-xl font-semibold text-gray-900 mt-4">Completing Clever Login...</h2>
          <p className="text-gray-600 mt-2">Please wait while we complete your authentication.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-whiteGrayBackground flex flex-col justify-center items-center font-sofiaSans">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Authentication Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/auth/teacher')}
            className="w-full bg-secondaryStoryAdditionalLightBlueText text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => window.history.back()}
            className="w-full mt-3 text-secondaryStoryAdditionalLightBlueText py-2 px-4 rounded-lg border border-secondaryStoryAdditionalLightBlueText hover:bg-blue-50 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default CleverCallback;
